<template>
  <div class="tools-collection">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">工具合集</h1>
      <p class="page-description">一站式工具导航，快速访问日常工具</p>
    </div>

    <!-- 搜索框 -->
    <div class="search-section">
      <el-input v-model="searchText" placeholder="搜索工具名称或描述..." clearable size="large" class="search-input">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>
    </div>

    <!-- 工具卡片网格 -->
    <div class="tools-grid">
      <div v-for="group in filteredGroups" :key="group.id" class="tool-group">
        <!-- 分组标题 -->
        <div class="group-header">
          <el-icon class="group-icon">
            <Tools v-if="group.icon === 'Tools'" />
            <Monitor v-else-if="group.icon === 'Monitor'" />
            <Setting v-else-if="group.icon === 'Setting'" />
            <Link v-else-if="group.icon === 'Link'" />
            <Document v-else-if="group.icon === 'Document'" />
            <Platform v-else-if="group.icon === 'Platform'" />
            <TrendCharts v-else-if="group.icon === 'TrendCharts'" />
            <Connection v-else-if="group.icon === 'Connection'" />
            <Edit v-else-if="group.icon === 'Edit'" />
            <Brush v-else-if="group.icon === 'Brush'" />
            <Tools v-else />
          </el-icon>
          <h3 class="group-title">{{ group.title }}</h3>
          <span class="group-description">{{ group.description }}</span>
        </div>

        <!-- 工具卡片 -->
        <div class="tools-container">
          <el-card v-for="tool in group.children" :key="tool.id" class="tool-card" :class="{ 'highlight-card': tool.badge }" shadow="hover" @click="handleToolClick(tool)">
            <div class="tool-content">
              <!-- 图标区域 -->
              <div class="tool-icon-wrapper">
                <el-icon class="tool-icon">
                  <Tools v-if="tool.icon === 'Tools'" />
                  <Monitor v-else-if="tool.icon === 'Monitor'" />
                  <Setting v-else-if="tool.icon === 'Setting'" />
                  <Link v-else-if="tool.icon === 'Link'" />
                  <Document v-else-if="tool.icon === 'Document'" />
                  <Platform v-else-if="tool.icon === 'Platform'" />
                  <TrendCharts v-else-if="tool.icon === 'TrendCharts'" />
                  <Connection v-else-if="tool.icon === 'Connection'" />
                  <Edit v-else-if="tool.icon === 'Edit'" />
                  <Brush v-else-if="tool.icon === 'Brush'" />
                  <Unlock v-else-if="tool.icon === 'Unlock'" />
                  <Tools v-else />
                </el-icon>
                <el-tag v-if="tool.badge" :type="getBadgeType(tool.badge)" class="tool-badge" size="small">
                  {{ tool.badge }}
                </el-tag>
              </div>

              <!-- 工具信息 -->
              <div class="tool-info">
                <h4 class="tool-title">{{ tool.title }}</h4>
                <p class="tool-description">{{ tool.description }}</p>
              </div>

              <!-- 操作按钮 -->
              <div class="tool-action">
                <el-button :type="tool.type === 'external' ? 'primary' : 'default'" circle size="small">
                  <el-icon>
                    <component :is="tool.type === 'external' ? Link : Right" />
                  </el-icon>
                </el-button>
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="filteredGroups.length === 0" class="empty-state">
      <el-empty description="未找到相关工具" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

import { toolsConfig } from '@/mock/toolsConfig.js'
import {
  Search,
  Monitor,
  Setting,
  Link,
  Document,
  Platform,
  TrendCharts,
  Connection,
  Tools,
  Edit,
  Brush,
  Right,
  Unlock
} from '@element-plus/icons-vue'

const router = useRouter()
const searchText = ref('')

// 计算属性：过滤工具组
const filteredGroups = computed(() => {
  if (!searchText.value) {
    return toolsConfig
  }

  const searchLower = searchText.value.toLowerCase()
  return toolsConfig
    .map(group => {
      const filteredChildren = group.children.filter(tool =>
        tool.title.toLowerCase().includes(searchLower) ||
        tool.description.toLowerCase().includes(searchLower)
      )

      return {
        ...group,
        children: filteredChildren
      }
    })
    .filter(group => group.children.length > 0)
})

// 处理工具点击事件
const handleToolClick = (tool) => {
  if (!tool.url && !tool.route) {
    ElMessage.warning('该工具尚未完成，请等待上线！');
    return;
  }
  if (tool.type === 'internal') {
    // 内部路由跳转
    router.push(tool.route)
  } else if (tool.type === 'external') {
    // 外部链接打开
    if (tool.target === '_blank') {
      window.open(tool.url, '_blank')
    } else {
      window.location.href = tool.url
    }
  }
}

// 获取徽章类型
const getBadgeType = (badge) => {
  const badgeTypes = {
    '热门': 'danger',
    '新': 'success',
    '推荐': 'warning'
  }
  return badgeTypes[badge] || 'info'
}


onMounted(() => {
  console.log('Tools合集页面加载完成')
})
</script>

<style scoped>
.tools-collection {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  min-height: calc(100vh - 120px);
}

/* 页面标题样式 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-description {
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0;
}

/* 搜索区域样式 */
.search-section {
  margin-bottom: 32px;
}

.search-input {
  max-width: 400px;
  margin: 0 auto;
}

/* 工具网格样式 */
.tools-grid {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* 分组头部样式 */
.group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f3f4f6;
}

.group-icon {
  font-size: 24px;
  color: #667eea;
  background: #f1f5f9;
  padding: 8px;
  border-radius: 8px;
}

.group-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.group-description {
  color: #6b7280;
  font-size: 0.9rem;
  margin-left: auto;
}

/* 工具容器样式 */
.tools-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

/* 工具卡片样式 */
.tool-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.tool-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
  cursor: pointer;
}

.tool-card:hover .tool-title {
  color: #667eea;
}

.tool-card.highlight-card {
  border-left: 4px solid #667eea;
}

.tool-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
}

.tool-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.tool-card:hover .tool-icon-wrapper {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
}

.tool-icon {
  font-size: 24px;
  color: #667eea;
  transition: color 0.3s ease;
}

.tool-card:hover .tool-icon {
  color: white;
}

.tool-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-icon svg {
  width: 24px;
  height: 24px;
  fill: currentColor;
}

.group-icon svg {
  width: 24px;
  height: 24px;
  fill: currentColor;
}

.tool-badge {
  position: absolute;
  top: -8px;
  right: -8px;
}

.tool-info {
  flex: 1;
  min-width: 0;
}

.tool-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tool-description {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tool-action {
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.tool-card:hover .tool-action {
  opacity: 1;
  transform: scale(1.1);
}

/* 空状态样式 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .tools-collection {
    padding: 16px;
  }

  .page-title {
    font-size: 2rem;
  }

  .tools-container {
    grid-template-columns: 1fr;
  }

  .group-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .group-description {
    margin-left: 0;
  }
}
</style>
